import React from 'react';
import BarSplitChart from '../../dashboard-overview/components/BarSplitChart';
import DonutSplitChart from '../../dashboard-overview/components/DonutSplitChart';
import ReportTable from './ReportTable';
import SectionToggle from './SectionToggle';

const CARD = 'rounded-md border border-dash-border bg-dash-card p-4';

// Chart drops in against the existing /reports/mis/distribution endpoint:
// DistRow.value is the LIVE count by backend design, so single-series charts
// render Live-only automatically. Top-N is server-side via `limit`.
// Every card gets the Table/Chart toggle: long-label dimensions are safe as
// bars now that Y-axis ticks truncate to the axis budget (full name stays in
// the tooltip).
// Order groups like with like: the three donuts share the first row and the
// bar charts fill the rest. A grid row is as tall as its tallest card, so a
// ~200px donut card beside a 12-row bar chart used to get stretched with dead
// space above and below the ring.
const DIMENSIONS = [
  { dim: 'region', title: 'Split over Region', chart: 'donut' },
  { dim: 'msme', title: 'Split over MSME', chart: 'donut' },
  { dim: 'category', title: 'Split over MSME Category', chart: 'donut', foldCase: true },
  { dim: 'currency', title: 'Split over Currency', chart: 'bar', barColor: 'var(--dash-chart-2)' },
  { dim: 'country', title: 'Split over Country', chart: 'bar', barColor: 'var(--dash-chart-3)' },
  { dim: 'payment_terms', title: 'Split over Payment Terms', chart: 'bar', barColor: 'var(--dash-chart-1)' },
  { dim: 'supplier_group', title: 'Split over Supplier Group', chart: 'bar', barColor: 'var(--dash-chart-4)' },
  { dim: 'delivery_terms', title: 'Split over Delivery Terms', chart: 'bar', barColor: 'var(--dash-chart-6)' },
  { dim: 'commodity_code', title: 'Split over Commodity Code', chart: 'bar', barColor: 'var(--dash-chart-5)' },
];

// Merge case-variant category names (Micro/micro) on the chart. Folds on `value`
// (Live), so the donut center (sum of value) stays a Live-only, unblended total.
const foldCase = (rows) => {
  const m = new Map();
  for (const r of rows) {
    const key = String(r.name || '').trim().toLowerCase();
    const label = key.charAt(0).toUpperCase() + key.slice(1);
    m.set(label, (m.get(label) || 0) + (Number(r.value) || 0));
  }
  return [...m].map(([name, value]) => ({ name, value }));
};

const DistributionCard = ({ dim, title, chart, barColor, foldCase: fold, filters }) => {
  const [view, setView] = React.useState('chart');
  const showChart = !!chart && view === 'chart';

  return (
    <div className={CARD}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-dash-foreground">{title}</h3>
        {chart ? <SectionToggle value={view} onChange={setView} /> : null}
      </div>
      {showChart ? (
        chart === 'bar' ? (
          <BarSplitChart
            title=""
            endpoint="/reports/mis/distribution"
            params={{ dimension: dim, limit: 10, ...filters }}
            barColor={barColor || 'var(--dash-chart-1)'}
            showPercent
          />
        ) : (
          <DonutSplitChart
            title=""
            endpoint="/reports/mis/distribution"
            params={{ dimension: dim, ...filters }}
            transform={fold ? foldCase : undefined}
            cornerLabel="live"
          />
        )
      ) : (
        <ReportTable dimension={dim} filters={filters} />
      )}
    </div>
  );
};

const DistributionSection = ({ filters }) => (
  <section className="flex flex-col gap-3">
    <h2 className="text-sm font-semibold text-dash-foreground">Distribution Detail</h2>
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
      {DIMENSIONS.map((d) => (
        <DistributionCard key={d.dim} {...d} filters={filters} />
      ))}
    </div>
  </section>
);

export default DistributionSection;
